import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input, InputGroup } from '@/components/ui/catalyst/input';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { MailRequirement } from '@/services/api/generated/policeDepartmentsService';

import { ChannelFormData, FormErrors, MAIL_REQUIREMENTS } from './types';

interface CompactMailingSectionProps {
  formData: ChannelFormData;
  errors: FormErrors;
  onChange: (
    field: keyof ChannelFormData,
    value: string | MailRequirement[],
  ) => void;
}

export const CompactMailingSection = ({
  formData,
  errors,
  onChange,
}: CompactMailingSectionProps) => {
  const handleRequirementToggle = (
    requirement: MailRequirement,
    checked: boolean,
  ) => {
    const newItems = checked
      ? [...formData.requiredItems, requirement]
      : formData.requiredItems.filter((i) => i !== requirement);
    onChange('requiredItems', newItems);
  };

  const handleFeeAmountChange = (value: string) => {
    // Allow empty string, numbers, and decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      const numValue = Number(value);
      // Enforce maximum of 60
      if (value === '' || numValue <= 60) {
        onChange('feeAmount', value);
      }
    }
  };

  const handleFeeAmountBlur = () => {
    // Format to 2 decimal places on blur if there's a value
    if (formData.feeAmount && !Number.isNaN(Number(formData.feeAmount))) {
      const numValue = Number(formData.feeAmount);
      // Cap at 60 if exceeded
      const capped = Math.min(numValue, 60);
      const formatted = capped.toFixed(2);
      onChange('feeAmount', formatted);
    }
  };

  return (
    <div className="space-y-6">
      {/* Address Section */}
      <div className="rounded-lg bg-gray-50 p-4">
        <Label className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-700">
          Mailing Address
        </Label>
        <div className="space-y-4">
          <Field>
            <Label className="text-sm font-medium text-gray-700">
              Street Address
            </Label>
            <Input
              value={formData.addressLine1}
              onChange={(e) => onChange('addressLine1', e.target.value)}
              placeholder="e.g., 123 Main Street"
              invalid={!!errors.addressLine1}
            />
            {errors.addressLine1 && (
              <p className="mt-1 text-xs text-red-600">{errors.addressLine1}</p>
            )}
          </Field>

          <Field>
            <Label className="text-sm font-medium text-gray-700">
              Address Line 2{' '}
              <span className="font-normal text-gray-500">(optional)</span>
            </Label>
            <Input
              value={formData.addressLine2}
              onChange={(e) => onChange('addressLine2', e.target.value)}
              placeholder="Apt, suite, attn:"
            />
          </Field>

          <div className="grid grid-cols-12 gap-3">
            <Field className="col-span-6">
              <Label className="text-sm font-medium text-gray-700">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => onChange('city', e.target.value)}
                placeholder="e.g., San Francisco"
                invalid={!!errors.city}
              />
              {errors.city && (
                <p className="mt-1 text-xs text-red-600">{errors.city}</p>
              )}
            </Field>

            <Field className="col-span-3">
              <Label className="text-sm font-medium text-gray-700">State</Label>
              <Input
                value={formData.state}
                onChange={(e) =>
                  onChange('state', e.target.value.toUpperCase())
                }
                placeholder="CA"
                maxLength={2}
                invalid={!!errors.state}
                className="uppercase"
              />
              {errors.state && (
                <p className="mt-1 text-xs text-red-600">{errors.state}</p>
              )}
            </Field>

            <Field className="col-span-3">
              <Label className="text-sm font-medium text-gray-700">ZIP</Label>
              <Input
                value={formData.postalCode}
                onChange={(e) => onChange('postalCode', e.target.value)}
                placeholder="94102"
                invalid={!!errors.postalCode}
              />
              {errors.postalCode && (
                <p className="mt-1 text-xs text-red-600">{errors.postalCode}</p>
              )}
            </Field>
          </div>
        </div>
      </div>

      {/* Check Details Section */}
      <div className="rounded-lg bg-gray-50 p-4">
        <Label className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-700">
          Check Payment
        </Label>
        <div className="space-y-4">
          <Field>
            <Label className="text-sm font-medium text-gray-700">
              Payable To
            </Label>
            <Input
              value={formData.payableTo}
              onChange={(e) => onChange('payableTo', e.target.value)}
              placeholder="e.g., City of San Francisco"
              invalid={!!errors.payableTo}
            />
            {errors.payableTo && (
              <p className="mt-1 text-xs text-red-600">{errors.payableTo}</p>
            )}
          </Field>

          <Field>
            <Label className="text-sm font-medium text-gray-700">
              Fee Amount
            </Label>
            <InputGroup>
              <span
                data-slot="icon"
                className="flex items-center justify-center text-base font-semibold text-gray-500"
              >
                $
              </span>
              <Input
                type="text"
                inputMode="decimal"
                value={formData.feeAmount}
                onChange={(e) => handleFeeAmountChange(e.target.value)}
                onBlur={handleFeeAmountBlur}
                placeholder="0.00"
                className="text-base font-semibold"
                invalid={!!errors.feeAmount}
              />
            </InputGroup>
            {errors.feeAmount ? (
              <p className="mt-1 text-xs text-red-600">{errors.feeAmount}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">Maximum: $60.00</p>
            )}
          </Field>

          <Field>
            <Label className="text-sm font-medium text-gray-700">
              Memo Line Instructions{' '}
              <span className="font-normal text-gray-500">(optional)</span>
            </Label>
            <Input
              value={formData.memoInstructions}
              onChange={(e) => onChange('memoInstructions', e.target.value)}
              placeholder="e.g., Police Report Request"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label className="text-sm font-medium text-gray-700">
                Wait Period
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  value={formData.waitDays}
                  onChange={(e) => onChange('waitDays', e.target.value)}
                  placeholder="0"
                  className="pr-14"
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">
                  days
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Days to wait after incident
              </p>
            </Field>

            <Field>
              <Label className="text-sm font-medium text-gray-700">
                Processing Time
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  value={formData.processingDays}
                  onChange={(e) => onChange('processingDays', e.target.value)}
                  placeholder="0"
                  className="pr-14"
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">
                  days
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Expected turnaround time
              </p>
            </Field>
          </div>
        </div>
      </div>

      {/* Requirements Section */}
      <div className="rounded-lg bg-gray-50 p-4">
        <Label className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-700">
          Required Items
        </Label>
        <div className="space-y-2.5">
          {MAIL_REQUIREMENTS.map((req) => (
            <CheckboxField key={req.value}>
              <Checkbox
                checked={formData.requiredItems.includes(req.value)}
                onChange={(checked) =>
                  handleRequirementToggle(req.value, checked)
                }
              />
              <Label className="text-sm text-gray-700">{req.label}</Label>
            </CheckboxField>
          ))}
        </div>

        <Field className="mt-4">
          <Label className="text-sm font-medium text-gray-700">
            Special Requirements{' '}
            <span className="font-normal text-gray-500">(optional)</span>
          </Label>
          <Textarea
            value={formData.specialRequirements}
            onChange={(e) => onChange('specialRequirements', e.target.value)}
            placeholder="Any additional requirements or special instructions..."
            rows={3}
          />
        </Field>
      </div>
    </div>
  );
};
